import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Clock, Info, Loader2, Mail, Pencil, Plus, Sparkles, Trash2, X } from "lucide-react";
import {
  createStep,
  deleteAiVariable,
  errorMessage,
  getVariableFill,
  updateStep,
  type AiVariable,
  type CampaignDetail,
  type CampaignStep,
  type DelayUnit,
  type EmailVariable,
  type VariableFill,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { AiVariableDialog } from "@/components/email/AiVariableDialog";
import { PreviewPanel } from "@/components/email/PreviewPanel";
import { tokenClass } from "@/components/email/TemplateText";
import { VariablePicker } from "@/components/email/VariablePicker";
import { insertToken, stepDays, tokensIn, tokensWithoutFallback } from "@/lib/email";
import { cn } from "@/lib/utils";

interface Form {
  name: string;
  delay_value: number;
  delay_unit: DelayUnit;
  subject_template: string;
  body_template: string;
}

function formOf(step: CampaignStep | null, campaign: CampaignDetail, position: number): Form {
  if (step) {
    return {
      name: step.name,
      delay_value: step.delay_value,
      delay_unit: step.delay_unit,
      subject_template: step.subject_template,
      body_template: step.body_template,
    };
  }
  const first = position === 1;
  return {
    name: first ? "First email" : `Follow-up ${position - 1}`,
    delay_value: first ? 0 : campaign.settings.default_delay_days,
    delay_unit: "DAYS",
    subject_template: first ? "{{contact.first_name}}, une question sur {{company.name}}" : "",
    body_template: first
      ? "Bonjour {{contact.first_name}},\n\n\n\nBien à vous,"
      : "Bonjour {{contact.first_name}},\n\nJe me permets de revenir vers vous au sujet de mon précédent message.\n\nBien à vous,",
  };
}

const sameForm = (a: Form, b: Form) =>
  a.name === b.name &&
  a.delay_value === b.delay_value &&
  a.delay_unit === b.delay_unit &&
  a.subject_template === b.subject_template &&
  a.body_template === b.body_template;

/** The template editor: left, the step; right, a real preview. */
export function StepEditorSheet({
  open,
  onOpenChange,
  campaign,
  step,
  newPosition,
  variables,
  canEdit,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: CampaignDetail;
  /** null = a new step at `newPosition`. */
  step: CampaignStep | null;
  newPosition: number;
  variables: EmailVariable[];
  canEdit: boolean;
  onSaved: (campaign: CampaignDetail | null) => void;
}) {
  const confirm = useConfirm();
  const [current, setCurrent] = useState<CampaignStep | null>(step);
  const [form, setForm] = useState<Form>(() => formOf(step, campaign, newPosition));
  const [baseline, setBaseline] = useState<Form>(form);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiDialog, setAiDialog] = useState<{ open: boolean; variable: AiVariable | null }>({ open: false, variable: null });
  const [fill, setFill] = useState<VariableFill | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastFocus = useRef<"subject" | "body">("body");
  // Where the caret goes once an inserted token is in the text: placed as
  // soon as React has written it, so the next key typed lands there.
  const pendingCaret = useRef<{ target: "subject" | "body"; caret: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const initial = formOf(step, campaign, newPosition);
    setCurrent(step);
    setForm(initial);
    setBaseline(initial);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset per opening
  }, [open, step?.id]);

  // How often each variable has a value among the contacts this email reaches.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getVariableFill(campaign.project_slug, campaign.id)
      .then((result) => !cancelled && setFill(result))
      .catch(() => !cancelled && setFill(null));
    return () => {
      cancelled = true;
    };
  }, [open, campaign.project_slug, campaign.id]);

  const position = current?.position ?? newPosition;
  const dirty = !current || !sameForm(form, baseline);
  const isNew = !current;
  const aiVariables = current?.ai_variables ?? [];
  const aiKeys = aiVariables.map((v) => v.key);
  const days = stepDays(campaign.steps);
  const day =
    current && days[current.position - 1] !== undefined
      ? days[current.position - 1]
      : (days[days.length - 1] ?? 0) + (position === 1 ? form.delay_value : form.delay_value * (form.delay_unit === "WEEKS" ? 7 : 1));

  // Tokens the template uses that this project type does not offer.
  const known = useMemo(() => new Set([...variables.map((v) => v.token), ...aiKeys.map((k) => `ai.${k}`)]), [variables, aiKeys]);
  const used = tokensIn(form.subject_template, form.body_template);
  const unknown = used.filter((t) => !known.has(t));
  // Used without a fallback while some contacts have no value: those are not sent.
  const gaps = fill?.considered
    ? tokensWithoutFallback(form.subject_template, form.body_template)
        .filter((t) => t in fill.filled && fill.filled[t] < fill.considered)
        .map((t) => ({ token: t, percent: Math.min(99, Math.round((fill.filled[t] / fill.considered) * 100)) }))
    : [];

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }));

  function insert(token: string, target: "subject" | "body" = lastFocus.current, withFallback = false) {
    const el = target === "subject" ? subjectRef.current : bodyRef.current;
    const field = target === "subject" ? "subject_template" : "body_template";
    const text = form[field];
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = insertToken(text, start, end, token, withFallback);
    pendingCaret.current = { target, caret: next.caret };
    set(field, next.text);
  }

  useLayoutEffect(() => {
    const pending = pendingCaret.current;
    if (!pending) return;
    pendingCaret.current = null;
    const el = pending.target === "subject" ? subjectRef.current : bodyRef.current;
    el?.focus();
    el?.setSelectionRange(pending.caret, pending.caret);
  }, [form]);

  const save = useCallback(async (): Promise<CampaignStep | null> => {
    if (!canEdit) return null;
    if (!form.name.trim()) {
      setError("Name the step.");
      return null;
    }
    setSaving(true);
    setError(null);
    try {
      if (current) {
        const updated = await updateStep(campaign.project_slug, current.id, form);
        setCurrent(updated);
        setBaseline(form);
        toast.success("Step saved");
        onSaved(null);
        return updated;
      }
      const before = new Set(campaign.steps.map((s) => s.id));
      const detail = await createStep(campaign.project_slug, campaign.id, { ...form, position: newPosition });
      const created = detail.steps.find((s) => !before.has(s.id)) ?? null;
      setCurrent(created);
      setBaseline(form);
      toast.success("Step added");
      onSaved(detail);
      return created;
    } catch (err) {
      setError(errorMessage(err, "The step could not be saved."));
      return null;
    } finally {
      setSaving(false);
    }
  }, [canEdit, form, current, campaign, newPosition, onSaved]);

  // Cmd/Ctrl+S saves.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (dirty && !saving) void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dirty, saving, save]);

  async function close() {
    if (canEdit && dirty && !(isNew && sameForm(form, baseline))) {
      const discard = await confirm({
        title: "Discard your changes?",
        description: "This step has changes that are not saved.",
        confirmLabel: "Discard",
        variant: "warning",
      });
      if (!discard) return;
    }
    onOpenChange(false);
  }

  async function openAiDialog(variable: AiVariable | null) {
    if (!current) {
      const created = await save();
      if (!created) return;
    }
    setAiDialog({ open: true, variable });
  }

  async function removeAi(variable: AiVariable) {
    const ok = await confirm({
      title: `Delete ai.${variable.key}?`,
      description: used.includes(`ai.${variable.key}`)
        ? "The template still uses it: the email will not be sendable until you remove the token."
        : "Its prompt is deleted. Values already sent stay in each contact's history.",
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      setCurrent(await deleteAiVariable(campaign.project_slug, variable.id));
      onSaved(null);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? onOpenChange(true) : void close())}>
      <SheetContent
        className="flex w-[min(1240px,97vw)] flex-col gap-0 p-0 outline-none sm:max-w-none [&>button.absolute]:hidden"
        onOpenAutoFocus={(event) => {
          // Focus the editor, not the step name with its text selected.
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
      >
        {/* Header */}
        <header className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-6 py-3.5">
          <span
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
              position === 1 ? "bg-indigo-600 text-white shadow-cta" : "bg-indigo-50 text-indigo-600",
            )}
          >
            {position}
          </span>
          <div className="min-w-0 flex-1">
            <SheetTitle className="sr-only">{isNew ? "New step" : `Edit step ${position}`}</SheetTitle>
            <SheetDescription className="text-xs text-slate-500">
              Step {position} · Day {day} · {campaign.name}
            </SheetDescription>
            <input
              value={form.name}
              disabled={!canEdit}
              onChange={(e) => set("name", e.target.value)}
              maxLength={255}
              aria-label="Step name"
              className="w-full truncate border-0 bg-transparent p-0 text-lg font-semibold text-slate-900 outline-none placeholder:text-slate-300 focus:ring-0"
              placeholder="Name this step"
            />
          </div>
          {canEdit && dirty && !isNew && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-200">Unsaved changes</span>
          )}
          {canEdit && !dirty && current && (
            <span className="text-xs text-slate-400">Saved · v{current.version}</span>
          )}
          {canEdit && (
            <Button variant="primary" size="sm" disabled={!dirty || saving} onClick={() => void save()}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isNew ? "Add step" : "Save"}
              <kbd className="ml-1 hidden rounded bg-white/20 px-1 text-[10px] font-medium sm:inline">⌘S</kbd>
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400" aria-label="Close" onClick={() => void close()}>
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_minmax(380px,0.8fr)]">
          {/* Editor */}
          <div className="min-h-0 space-y-4 overflow-y-auto bg-slate-50/50 px-6 py-5">
            {campaign.sequence.active_sequence && (
              <div className="flex gap-2.5 rounded-xl border border-sky-100 bg-sky-50/70 px-3.5 py-2.5 text-xs leading-relaxed text-sky-900">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <p>
                  {isNew || current?.capabilities?.pending
                    ? "This follow-up goes after the last step. At the next sync, each contact who hasn't passed it gets their own email for it before Lemlist can send it; contacts who completed the sequence, replied or were removed never get it."
                    : "Contacts are already in Lemlist: a saved change reaches only those who have not been sent this step yet, at the next sync. Emails already sent never change."}
                </p>
              </div>
            )}

            <EditorSection
              icon={<Clock className="h-4 w-4" />}
              title={position === 1 ? "When it is sent" : "When it follows"}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={365}
                  value={form.delay_value}
                  disabled={!canEdit}
                  onChange={(e) => set("delay_value", Math.max(0, Number(e.target.value)))}
                  className="h-9 w-20"
                  aria-label="Delay"
                />
                <Select value={form.delay_unit} onValueChange={(v) => set("delay_unit", v as DelayUnit)} disabled={!canEdit}>
                  <SelectTrigger className="h-9 w-[110px]" aria-label="Delay unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DAYS">{form.delay_value === 1 ? "day" : "days"}</SelectItem>
                    <SelectItem value="WEEKS">{form.delay_value === 1 ? "week" : "weeks"}</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-500">
                  {position === 1 ? "after the contact is handed to Lemlist" : "after the previous email"}
                </span>
              </div>
            </EditorSection>

            <EditorSection
              icon={<Mail className="h-4 w-4" />}
              title="Email"
              hint="Variables are replaced for each contact: the preview on the right shows the result."
            >
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="step-subject" className="text-xs font-medium text-slate-600">
                    Subject
                  </Label>
                  {canEdit && (
                    <VariablePicker
                      variables={variables}
                      aiKeys={aiKeys}
                      fill={fill}
                      onPick={(t, withFallback) => insert(t, "subject", withFallback)}
                    />
                  )}
                </div>
                <Input
                  id="step-subject"
                  ref={subjectRef}
                  value={form.subject_template}
                  disabled={!canEdit}
                  maxLength={500}
                  onFocus={() => (lastFocus.current = "subject")}
                  onChange={(e) => set("subject_template", e.target.value)}
                  placeholder={position === 1 ? "{{contact.first_name}}, une question sur {{company.name}}" : "Leave empty to reply in the same thread"}
                  className="h-10 rounded-xl border-slate-200 font-medium"
                />
                {position > 1 && (
                  <p className="text-[11px] text-slate-400">
                    Empty, the follow-up is sent as a reply in the thread of the previous email.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label htmlFor="step-body" className="text-xs font-medium text-slate-600">
                    Body
                  </Label>
                  {canEdit && (
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      {aiKeys.map((key) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => insert(`ai.${key}`, "body")}
                          title={`Insert {{ai.${key}}} at the cursor`}
                          className="inline-flex h-7 items-center gap-1 rounded-md bg-fuchsia-50 px-2 font-mono text-[11px] text-fuchsia-700 ring-1 ring-inset ring-fuchsia-200/70 transition-colors hover:bg-fuchsia-100"
                        >
                          <Plus className="h-3 w-3" />
                          ai.{key}
                        </button>
                      ))}
                      <VariablePicker
                        variables={variables}
                        aiKeys={aiKeys}
                        fill={fill}
                        onPick={(t, withFallback) => insert(t, "body", withFallback)}
                      />
                    </div>
                  )}
                </div>
                <Textarea
                  id="step-body"
                  ref={bodyRef}
                  value={form.body_template}
                  disabled={!canEdit}
                  maxLength={20000}
                  onFocus={() => (lastFocus.current = "body")}
                  onChange={(e) => set("body_template", e.target.value)}
                  rows={14}
                  className="min-h-[300px] resize-y rounded-xl border-slate-200 text-[14px] leading-relaxed"
                />
                <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                  {used.length ? (
                    <>
                      <span className="text-[11px] text-slate-400">Uses</span>
                      {used.map((token) => (
                        <span
                          key={token}
                          title={known.has(token) ? undefined : "Not a variable of this project type"}
                          className={cn(
                            "rounded px-1.5 py-0.5 font-mono text-[11px] ring-1 ring-inset",
                            known.has(token) ? tokenClass(token) : "bg-rose-50 text-rose-600 ring-rose-200",
                          )}
                        >
                          {token}
                        </span>
                      ))}
                    </>
                  ) : (
                    <span className="text-[11px] text-slate-400">No variable yet — insert one at the cursor.</span>
                  )}
                </div>
                {unknown.length > 0 && (
                  <p className="text-xs text-rose-600">
                    {unknown.map((t) => `{{${t}}}`).join(", ")} {unknown.length === 1 ? "is" : "are"} not available here:
                    the email would not be sendable.
                  </p>
                )}
                {gaps.length > 0 && (
                  <div className="space-y-0.5 rounded-lg bg-amber-50/70 px-2.5 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-200/70">
                    {gaps.map((gap) => (
                      <p key={gap.token}>
                        <span className="font-mono">{`{{${gap.token}}}`}</span> has a value for {gap.percent}% of the{" "}
                        {fill?.population === "campaign" ? "campaign's contacts" : "project's contacts"}: the others are
                        not sent.
                      </p>
                    ))}
                    <p className="text-amber-700/80">
                      Give it a fallback — <span className="font-mono">{'{{token | "text"}}'}</span>, or “+ fallback” in
                      Variables — or pick another variable.
                    </p>
                  </div>
                )}
              </div>
            </EditorSection>

            <EditorSection
              tone="ai"
              icon={<Sparkles className="h-4 w-4" />}
              title="AI passages"
              hint="A passage written for each contact from a prompt, then inserted like any variable."
              action={
                canEdit && (
                  <Button size="sm" variant="outline" className="shrink-0 bg-white" onClick={() => void openAiDialog(null)}>
                    <Plus className="h-3.5 w-3.5" />
                    New AI variable
                  </Button>
                )
              }
            >
              {aiVariables.length ? (
                <ul className="space-y-2">
                  {aiVariables.map((variable) => (
                    <li key={variable.id} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-fuchsia-50 px-1.5 py-0.5 font-mono text-xs text-fuchsia-700 ring-1 ring-inset ring-fuchsia-200/70">
                          {`{{ai.${variable.key}}}`}
                        </span>
                        {!used.includes(`ai.${variable.key}`) && (
                          <span className="text-[11px] text-amber-600">not used in the email yet</span>
                        )}
                        {canEdit && (
                          <span className="ml-auto flex items-center gap-0.5">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" aria-label={`Edit ai.${variable.key}`} onClick={() => void openAiDialog(variable)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-rose-600" aria-label={`Delete ai.${variable.key}`} onClick={() => void removeAi(variable)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 line-clamp-2 text-xs text-slate-500">{variable.prompt_template.replace(/\s+/g, " ").trim()}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Info className="h-3.5 w-3.5" />
                  {isNew ? "The step is saved when you create its first AI variable." : "No AI passage in this email."}
                </p>
              )}
            </EditorSection>

            {error && <p className="text-sm text-rose-600">{error}</p>}
          </div>

          {/* Preview */}
          <aside className="min-h-0 overflow-y-auto border-t border-slate-100 bg-[#f7f7fb] px-6 py-5 lg:border-l lg:border-t-0">
            <PreviewPanel
              slug={campaign.project_slug}
              campaignId={campaign.id}
              stepId={current?.id ?? null}
              position={position}
              subject={form.subject_template}
              body={form.body_template}
              aiVariables={aiVariables}
              saved={!dirty}
            />
          </aside>
        </div>

        <AiVariableDialog
          open={aiDialog.open}
          onOpenChange={(o) => setAiDialog((s) => ({ ...s, open: o }))}
          step={current}
          variable={aiDialog.variable}
          variables={variables}
          fill={fill}
          slug={campaign.project_slug}
          campaignId={campaign.id}
          onSaved={(updated, key) => {
            setCurrent(updated);
            onSaved(null);
            if (!aiDialog.variable && !used.includes(`ai.${key}`)) {
              toast.message(`Insert {{ai.${key}}} where the passage belongs.`);
            }
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

/** One part of the step editor: an icon, a title, what it is for, its fields. */
function EditorSection({
  icon,
  title,
  hint,
  action,
  tone = "default",
  children,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
  tone?: "default" | "ai";
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        "space-y-3 rounded-xl border bg-white p-4 shadow-sm",
        tone === "ai" ? "border-fuchsia-100" : "border-slate-200/70",
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
              tone === "ai" ? "bg-fuchsia-50 text-fuchsia-600" : "bg-indigo-50 text-indigo-600",
            )}
          >
            {icon}
          </span>
          <div className="min-w-0 pt-0.5">
            <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
            {hint && <p className="text-xs text-slate-500">{hint}</p>}
          </div>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}
